import {
  Component,
  HostListener,
  OnInit,
  OnDestroy,
  Renderer2,
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';

export interface SearchablePage {
  title: string;
  path: string;
  category: string;
  description: string;
  keywords: string[];
}

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {

  isScrolled      = false;
  mobileMenuOpen  = false;
  activeAccordion: string | null = null;

  // ── Home-only elements ─────────────────────────────────
  isHome      = false;
  searchOpen  = false;
  searchQuery = '';
  isHindi     = false;

  searchResults: SearchablePage[] = [];

  pages: SearchablePage[] = [
    // ABOUT US
    { title: 'Our History', path: '/about-us/our-history', category: 'About Us', description: 'Learn about the origin and milestones of Shah Satnam Ji Girls\' College, established in 1997.', keywords: ['history', 'origin', 'establishment', 'milestone', 'past', 'founding'] },
    { title: 'Vision & Mission', path: '/about-us/vision-mission', category: 'About Us', description: 'Explore our core values, strategic vision, and mission to empower women through high-quality education.', keywords: ['vision', 'mission', 'aim', 'goal', 'values', 'empowerment'] },
    { title: 'Principal\'s Message', path: '/about-us/principal-message', category: 'About Us', description: 'Read the warm welcome address and academic insights from our college Principal.', keywords: ['principal', 'message', 'welcome', 'address', 'leader', 'desk'] },
    { title: 'Code of Conduct', path: '/about-us/code-of-conduct', category: 'About Us', description: 'Browse the rules, academic guidelines, and ethical standards expected of students and staff.', keywords: ['rules', 'conduct', 'discipline', 'ethics', 'guidelines', 'regulations'] },
    { title: 'College Anthem', path: '/about-us/college-anthem', category: 'About Us', description: 'Listen to and view the official anthem of Shah Satnam Ji Girls\' College.', keywords: ['anthem', 'song', 'lyrics', 'audio', 'anthem play'] },
    { title: 'Prayer', path: '/about-us/prayer', category: 'About Us', description: 'Access the college prayer, daily spiritual chants, and lyrics.', keywords: ['prayer', 'chant', 'spiritual', 'lyrics', 'audio', 'morning'] },

    // HEI
    { title: 'About SSGC', path: '/hei/about-ssgc', category: 'HEI', description: 'General overview, accreditation, and affiliation details of the Higher Educational Institution.', keywords: ['hei', 'about', 'ssgc', 'institution', 'overview', 'details'] },
    { title: 'Act and Statutes', path: '/hei/act-statutes', category: 'HEI', description: 'Read the official regulatory acts, college statutes, and legal framework documents.', keywords: ['act', 'statutes', 'rules', 'regulations', 'legal', 'regulatory'] },
    { title: 'Institutional Development Plan', path: '/hei/institutional-dev-plan', category: 'HEI', description: 'Review our future roadmap, infrastructure growth strategy, and development plans.', keywords: ['development', 'plan', 'idp', 'roadmap', 'growth', 'future'] },
    { title: 'Affiliation University', path: '/hei/affiliation', category: 'HEI', description: 'Information regarding our official affiliation with Chaudhary Devi Lal University (CDLU), Sirsa.', keywords: ['affiliation', 'cdlu', 'university', 'recognized', 'registered'] },
    { title: 'Accreditation & Ranking', path: '/hei/ranking', category: 'HEI', description: 'Explore our NAAC grades, college rankings, and academic quality assurances.', keywords: ['naac', 'accreditation', 'ranking', 'grades', 'quality', 'assessment'] },
    { title: 'Annual Reports & Accounts', path: '/hei/annual-reports', category: 'HEI', description: 'Examine audit sheets, financial statements, and annual academic report records.', keywords: ['reports', 'accounts', 'annual', 'audit', 'finance', 'budget'] },

    // ADMINISTRATION
    { title: 'Governing Body', path: '/admin/governing-body', category: 'Administration', description: 'Meet the executive trustees and managing committee members steering our institution.', keywords: ['governing', 'body', 'trustees', 'board', 'management', 'committee'] },
    { title: 'Principal Desk', path: '/admin/principal', category: 'Administration', description: 'Official administrative profile and messages from the College Principal.', keywords: ['principal', 'desk', 'profile', 'administration', 'head'] },
    { title: 'Ombudsman', path: '/admin/ombudsman', category: 'Administration', description: 'Details about the appointed Ombudsperson for student grievance resolution.', keywords: ['ombudsman', 'grievance', 'neutral', 'resolutions', 'disputes'] },
    { title: 'Executive Council', path: '/admin/executive-council', category: 'Administration', description: 'Members list and objectives of the college executive council.', keywords: ['executive', 'council', 'members', 'board', 'committee'] },
    { title: 'Internal Complaint Committee (ICC)', path: '/admin/icc', category: 'Administration', description: 'Details of the ICC cell established to handle harassment complaints and safety.', keywords: ['icc', 'complaint', 'harassment', 'safety', 'internal', 'security'] },
    { title: 'Academic Leadership (HODs)', path: '/admin/hods', category: 'Administration', description: 'Profiles and contacts of various Academic Heads of Departments.', keywords: ['hods', 'heads', 'leadership', 'departments', 'academic'] },
    { title: 'Board of Studies', path: '/admin/board-of-studies', category: 'Administration', description: 'Curriculum design, academic boards, and syllabus approval committees.', keywords: ['board', 'studies', 'syllabus', 'curriculum', 'academics'] },
    { title: 'College Development Council (CDC)', path: '/admin/cdc', category: 'Administration', description: 'Executive body supervising college extension and affiliation updates.', keywords: ['cdc', 'development', 'council', 'extension', 'updates'] },

    // ACADEMICS
    { title: 'Academic Programs', path: '/academics/programs', category: 'Academics', description: 'Explore our undergraduate, postgraduate, and diploma courses in Arts, Science, and Commerce.', keywords: ['programs', 'courses', 'degree', 'pg', 'ug', 'diploma', 'ba', 'bsc', 'bcom', 'ma', 'mca'] },
    { title: 'Time Tables', path: '/academics/time-table', category: 'Academics', description: 'Get the latest classroom schedules and exam timetables for all departments.', keywords: ['schedule', 'timetable', 'calendar', 'classes', 'exams'] },
    { title: 'Skill / Value Added Courses', path: '/academics/value-added-courses', category: 'Academics', description: 'Enhance your career readiness with certification programs and skill-building modules.', keywords: ['skill', 'certification', 'added', 'value', 'career', 'vocational'] },
    { title: 'Syllabus', path: '/academics/syllabus', category: 'Academics', description: 'Download official course syllabi and learning outcomes.', keywords: ['syllabus', 'curriculum', 'subjects', 'pdf', 'courses'] },
    { title: 'Academic Calendar', path: '/academics/calendar', category: 'Academics', description: 'Stay updated on term dates, holidays, events, and examination schedules.', keywords: ['calendar', 'dates', 'holidays', 'exams', 'events', 'schedule'] },
    { title: 'Student Strength', path: '/academics/student-strength', category: 'Academics', description: 'Examine statistical data on student enrollment numbers across programs.', keywords: ['strength', 'students', 'enrollment', 'statistics', 'number'] },

    // FACULTY
    { title: 'Teaching Staff', path: '/faculty/teaching', category: 'Faculty', description: 'Meet our highly qualified and dedicated academic lecturers and professors.', keywords: ['teaching', 'faculty', 'lecturers', 'teachers', 'professors', 'staff'] },
    { title: 'Non-Teaching Staff', path: '/faculty/non-teaching', category: 'Faculty', description: 'Meet the helpful administrative and support staff members of our campus.', keywords: ['non-teaching', 'admin', 'office', 'support', 'staff', 'clerk'] },

    // ADMISSIONS
    { title: 'Prospectus', path: '/admissions/prospectus', category: 'Admissions', description: 'Download the official prospectus with detailed criteria and facility overviews.', keywords: ['prospectus', 'brochure', 'admissions', 'rules', 'fee', 'pdf'] },
    { title: 'UG Guidelines', path: '/admissions/ug', category: 'Admissions', description: 'Eligibility, counseling criteria, and guidelines for Undergraduate admissions.', keywords: ['ug', 'undergraduate', 'guidelines', 'eligibility', 'counseling'] },
    { title: 'PG Guidelines', path: '/admissions/pg', category: 'Admissions', description: 'Eligibility, counseling criteria, and guidelines for Postgraduate admissions.', keywords: ['pg', 'postgraduate', 'guidelines', 'eligibility', 'counseling'] },
    { title: 'Admission Registration', path: '/admissions/registration', category: 'Admissions', description: 'Register online to apply for academic admissions at our college.', keywords: ['registration', 'apply', 'form', 'admission', 'online'] },
    { title: 'Fees & Scholarships', path: '/admissions/fees-scholarships', category: 'Admissions', description: 'Information on academic fee structures, installment options, and scholarship schemes.', keywords: ['fees', 'scholarships', 'costs', 'charges', 'welfare', 'concessions'] },

    // STUDENT LIFE & FACILITIES
    { title: 'Smart Classrooms', path: '/campus-life/facilities/smart-classrooms', category: 'Student Life', description: 'Technologically advanced lecture halls with projector setups and audio-visual aids.', keywords: ['smart', 'classrooms', 'projectors', 'tech', 'digital', 'media'] },
    { title: 'Media Production & Editing Lab', path: '/campus-life/facilities/media-lab', category: 'Student Life', description: 'Equipped studio for journalism, mass communication, video editing, and recording.', keywords: ['media', 'lab', 'editing', 'journalism', 'recording', 'studio'] },
    { title: 'Computer Lab', path: '/campus-life/facilities/computer-lab', category: 'Student Life', description: 'Modern IT labs with high-speed internet and computing systems for IT classes.', keywords: ['computer', 'lab', 'it', 'software', 'internet', 'systems'] },
    { title: 'Science Lab', path: '/campus-life/facilities/science-lab', category: 'Student Life', description: 'State-of-the-art physics, chemistry, and biology experimental lab facilities.', keywords: ['science', 'lab', 'experiments', 'chemistry', 'physics', 'biology'] },
    { title: 'Geography Lab', path: '/campus-life/facilities/geography-lab', category: 'Student Life', description: 'Equipped with spatial maps, surveying equipment, and demographic charts.', keywords: ['geography', 'lab', 'maps', 'surveying', 'charts'] },
    { title: 'Psychology Lab', path: '/campus-life/facilities/psychology-lab', category: 'Student Life', description: 'Facilities for conducting tests, personality scales, and experimental research.', keywords: ['psychology', 'lab', 'counseling', 'behavior', 'tests'] },
    { title: 'Dance & Music Studio', path: '/campus-life/facilities/dance-room', category: 'Student Life', description: 'Dedicated performing arts studio with acoustic instruments and professional sound setups.', keywords: ['dance', 'music', 'studio', 'instruments', 'vocals', 'acoustic'] },
    { title: 'NCC', path: '/campus-life/ncc', category: 'Student Life', description: 'National Cadet Corps training, adventure camps, and youth empowerment activities.', keywords: ['ncc', 'cadets', 'defense', 'camps', 'national'] },
    { title: 'NSS', path: '/campus-life/nss', category: 'Student Life', description: 'National Service Scheme activities promoting community service and social leadership.', keywords: ['nss', 'service', 'community', 'social', 'volunteers'] },
    { title: 'Youth Red Cross', path: '/campus-life/youth-red-cross', category: 'Student Life', description: 'First aid training, blood donation camps, and disaster relief programs.', keywords: ['red cross', 'first aid', 'volunteers', 'health', 'social service'] },
    { title: 'Red Ribbon Club', path: '/campus-life/red-ribbon-club', category: 'Student Life', description: 'Awareness campaigns, healthcare seminars, and social youth clubs.', keywords: ['red ribbon', 'health', 'club', 'seminars', 'awareness'] },
    { title: 'Grievance Redressal & ICC', path: '/campus-life/grievances-icc', category: 'Student Life', description: 'Internal cells maintaining student welfare, safety, and addressing complaints.', keywords: ['grievances', 'icc', 'complaint', 'redressal', 'welfare', 'safety'] },
    { title: 'Campus Facilities', path: '/campus-life/campus-facilities', category: 'Student Life', description: 'General facilities including transport, hostels, cafeteria, and secure gates.', keywords: ['campus', 'facilities', 'transport', 'hostel', 'canteen', 'cafeteria', 'bus'] },
    { title: 'Green Campus', path: '/campus-life/green-campus', category: 'Student Life', description: 'Eco-friendly initiatives, water harvesting, solar grids, and clean power.', keywords: ['green', 'campus', 'solar', 'environment', 'eco', 'water', 'plants'] },
    { title: 'Sports Academy', path: '/student-facilities/sports', category: 'Student Life', description: 'Track record in athletics, yoga, national matches, and modern playground courts.', keywords: ['sports', 'athletics', 'playground', 'yoga', 'games', 'medals'] },

    // OTHER SECTIONS
    { title: 'MoUs & Collaborations', path: '/mous', category: 'Collaborations', description: 'Explore our academic and industrial ties, MoUs, and vocational partners.', keywords: ['mous', 'collaborations', 'industry', 'partners', 'training'] },
    { title: 'Internships', path: '/internships', category: 'Placements', description: 'Student internship schemes, corporate mentorships, and hands-on exposure.', keywords: ['internships', 'placement', 'training', 'mentorship', 'experience'] },
    { title: 'Placements', path: '/placements', category: 'Placements', description: 'Placement cell statistics, recruiter tie-ups, and student support details.', keywords: ['placements', 'recruiters', 'jobs', 'careers', 'opportunities'] },
    { title: 'Gallery', path: '/gallery', category: 'Media', description: 'Browse photo and video archives of events, functions, and campus life.', keywords: ['gallery', 'photos', 'images', 'videos', 'events', 'celebrations'] },
    { title: 'Contact Us', path: '/contact', category: 'Contact', description: 'Get coordinates, maps, email directories, and telephone listings for college queries.', keywords: ['contact', 'address', 'phone', 'email', 'location', 'reach'] },
    { title: 'FAQ', path: '/faq', category: 'Support', description: 'Answers to frequently asked questions about admissions, facilities, and academic rules.', keywords: ['faq', 'questions', 'support', 'help', 'answers'] }
  ];

  private isMobile    = false;
  private routerSub!: Subscription;

  constructor(private renderer: Renderer2, private router: Router) { }

  // ─────────────────────────────────────────────────────────
  //  LIFECYCLE
  // ─────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.checkBreakpoint();
    this.initTranslateState();
    this.trackRoute();
  }

  ngOnDestroy(): void {
    // BUG FIX: if component is destroyed while drawer is open, body scroll
    // was permanently locked. Always restore on teardown.
    this.renderer.removeStyle(document.body, 'overflow');
    this.routerSub?.unsubscribe();
  }

  // ─────────────────────────────────────────────────────────
  //  SCROLL
  // ─────────────────────────────────────────────────────────

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    this.isScrolled = window.scrollY > 60;
  }

  // ─────────────────────────────────────────────────────────
  //  RESIZE
  // ─────────────────────────────────────────────────────────

  @HostListener('window:resize', [])
  onWindowResize(): void {
    this.checkBreakpoint();
    // BUG FIX: rotating phone → desktop while drawer was open left the
    // drawer visible and body scroll locked. Close cleanly on breakpoint cross.
    if (!this.isMobile && this.mobileMenuOpen) {
      this.closeMobileMenu();
    }
  }

  private checkBreakpoint(): void {
    this.isMobile = window.innerWidth <= 1024;
  }

  // ─────────────────────────────────────────────────────────
  //  OUTSIDE CLICK
  // ─────────────────────────────────────────────────────────

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.mobileMenuOpen) return;

    const target = event.target as HTMLElement;

    // BUG FIX: original checked closest('#main-header') which is too broad —
    // a click anywhere in the header (including the hamburger) matched,
    // making the close/open race impossible to win. Now only the drawer
    // itself and the hamburger button are considered "inside".
    const insideDrawer = !!target.closest('.navbar-collapse');
    const onHamburger = !!target.closest('.navbar-toggler');

    if (!insideDrawer && !onHamburger) {
      this.closeMobileMenu();
    }
  }

  // ─────────────────────────────────────────────────────────
  //  ESCAPE KEY
  // ─────────────────────────────────────────────────────────

  @HostListener('document:keydown.escape', [])
  onEscapeKey(): void {
    if (this.mobileMenuOpen) {
      this.closeMobileMenu();
    }
  }

  // ─────────────────────────────────────────────────────────
  //  HAMBURGER
  // ─────────────────────────────────────────────────────────

  toggleMobileMenu(event: Event): void {
    // BUG FIX: without stopPropagation the document:click listener fires
    // on the same event and immediately closes the drawer that was just opened.
    event.stopPropagation();
    this.mobileMenuOpen ? this.closeMobileMenu() : this.openMobileMenu();
  }

  private openMobileMenu(): void {
    this.mobileMenuOpen = true;
    // BUG FIX: original wrote to document.body.style directly which bypasses
    // Angular's renderer and can cause issues in SSR / change detection.
    this.renderer.setStyle(document.body, 'overflow', 'hidden');
  }

  closeMobileMenu(): void {
    this.mobileMenuOpen = false;
    this.activeAccordion = null;
    this.renderer.removeStyle(document.body, 'overflow');
  }

  // ─────────────────────────────────────────────────────────
  //  ACCORDION
  // ─────────────────────────────────────────────────────────

  toggleAccordion(key: string, event: Event): void {
    event.stopPropagation();
    // BUG FIX: accordion was running on desktop too — stale activeAccordion
    // state prevented CSS :hover from showing menus correctly. Return early
    // on desktop so JS never touches dropdown state at that breakpoint.
    if (!this.isMobile) return;
    this.activeAccordion = this.activeAccordion === key ? null : key;
  }

  isAccordionOpen(key: string): boolean {
    return this.activeAccordion === key;
  }


  // ─────────────────────────────────────────────────────────
  //  ROUTE TRACKING (home-only elements)
  // ─────────────────────────────────────────────────────────

  private trackRoute(): void {
    // Set initial state
    const url = this.router.url;
    this.isHome = url === '/' || url === '';

    this.routerSub = this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe((e: any) => {
        const u: string = e.urlAfterRedirects ?? '';
        this.isHome = u === '/' || u === '';
        // BUG FIX: desktop dropdowns are CSS :hover-based.
        // After clicking a nav link, the cursor stays over the item so the
        // dropdown remains visible. Blurring the active element clears
        // :focus-within; triggering a synthetic mouseleave clears :hover.
        this.closeDropdowns();
      });
  }

  private closeDropdowns(): void {
    // Blur the currently focused element (clears :focus-within on dropdowns)
    (document.activeElement as HTMLElement)?.blur?.();

    // Dispatch a mouseleave event from every open dropdown-toggle so the
    // browser recalculates :hover state on the nav items.
    const openToggles = document.querySelectorAll<HTMLElement>(
      '#main-header .nav-item.dropdown > .nav-link'
    );
    openToggles.forEach(el => {
      el.dispatchEvent(new MouseEvent('mouseleave', { bubbles: true }));
    });

    // Also briefly move pointer events away by setting pointer-events:none on
    // the nav, then restoring — forces a repaint that clears :hover.
    const nav = document.querySelector<HTMLElement>('#main-header .main-nav');
    if (nav) {
      nav.style.pointerEvents = 'none';
      // Use requestAnimationFrame so the style is applied before reverting
      requestAnimationFrame(() => {
        nav.style.pointerEvents = '';
      });
    }
  }

  // ─────────────────────────────────────────────────────────
  //  GOOGLE TRANSLATE
  // ─────────────────────────────────────────────────────────

  private initTranslateState(): void {
    this.isHindi = this.getCookieValue('googtrans') === '/en/hi';
  }

  triggerTranslate(): void {
    const next = this.isHindi ? '/en/en' : '/en/hi';
    this.setCookie('googtrans', next);
    this.isHindi = !this.isHindi;
    window.location.reload();
  }

  private getCookieValue(name: string): string {
    const match = document.cookie
      .split('; ')
      .find(row => row.startsWith(`${name}=`));
    return match ? match.split('=').slice(1).join('=') : '';
  }

  private setCookie(name: string, value: string): void {
    const base = `${name}=${value}; path=/`;
    document.cookie = base;
    document.cookie = `${base}; domain=${location.hostname}`;
  }

  // ─────────────────────────────────────────────────────────
  //  SEARCH
  // ─────────────────────────────────────────────────────────

  toggleSearch(): void {
    this.searchOpen = !this.searchOpen;
    if (this.searchOpen) {
      setTimeout(() => {
        const input = document.querySelector('.search-input') as HTMLInputElement | null;
        input?.focus();
      }, 150);
    } else {
      this.searchQuery = '';
      this.searchResults = [];
    }
  }

  closeSearch(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.classList.contains('search-overlay')) {
      this.searchOpen = false;
      this.searchQuery = '';
      this.searchResults = [];
    }
  }

  closeSearchDirectly(): void {
    this.searchOpen = false;
    this.searchQuery = '';
    this.searchResults = [];
  }

  onSearchChange(): void {
    const query = this.searchQuery.trim().toLowerCase();
    if (!query) {
      this.searchResults = [];
      return;
    }
    this.searchResults = this.pages.filter(page =>
      page.title.toLowerCase().includes(query) ||
      page.description.toLowerCase().includes(query) ||
      page.category.toLowerCase().includes(query) ||
      page.keywords.some(kw => kw.toLowerCase().includes(query))
    );
  }

  doSearch(): void {
    this.onSearchChange();
  }
}